import { Formik, FormikHelpers } from "formik";
import { FC } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import yup from "app/util/yup";
import { useSendCustomReportVersionEmailMutation } from "app/api/graph/types";
import { reportVersionGraphId } from "app/api/graph/helpers";
import { handleError, handleSuccess } from "app/api/graph/form";
import { StatusAlert } from "app/component/form/StatusAlert";
import { RichTextEditor } from "app/component/form/text-editor/RichTextEditor";

type Props = {
    versionId: number;
    onSend?: () => void;
};

type FormValues = {
    subject: string;
    body: string;
    additionalRecipients?: string;
};

const schema: yup.ObjectSchema<FormValues> = yup.object({
    subject: yup.string().required(),
    body: yup.string().required(),
    additionalRecipients: yup.string().test("comma-separated-emails", "One or more emails are invalid", (value) => {
        if (value == null) {
            return true;
        }

        const emails = value
            .split(",")
            .map((e) => e.trim())
            .filter(Boolean);

        return emails.every((email) => yup.string().email().isValidSync(email));
    }),
});

const initialValues: FormValues = {
    subject: "",
    body: "",
};

export const SendCustomReportVersionEmailForm: FC<Props> = ({ versionId, onSend }) => {
    const [send] = useSendCustomReportVersionEmailMutation();

    const onSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) =>
        send({
            variables: {
                input: {
                    id: reportVersionGraphId(versionId),
                    subject: values.subject,
                    body: values.body,
                    additionalRecipients: values.additionalRecipients?.split(","),
                },
            },
        })
            .then(() => onSend?.())
            .then(() => handleSuccess(actions))
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    return (
        <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={initialValues}>
            {({
                handleSubmit,
                handleChange,
                handleBlur,
                setFieldValue,
                setFieldTouched,
                values,
                isSubmitting,
                errors,
                touched,
                status,
            }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <StatusAlert status={status as unknown} />

                    <Form.Group className="mb-3">
                        <Form.Label>Subject</Form.Label>
                        <Form.Control
                            type="text"
                            name="subject"
                            value={values.subject}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.subject && !!errors.subject}
                        />
                        <Form.Control.Feedback type="invalid">{errors.subject}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Body</Form.Label>
                        <RichTextEditor
                            value={values.body ?? ""}
                            onChange={(value) => setFieldValue("body", value)}
                            onBlur={() => setFieldTouched("body")}
                            isInvalid={touched.body && !!errors.body}
                        />
                        <Form.Control.Feedback type="invalid">{errors.body}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Additional recipients</Form.Label>
                        <Form.Control
                            type="text"
                            name="additionalRecipients"
                            value={values.additionalRecipients}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.additionalRecipients && !!errors.additionalRecipients}
                        />
                        <Form.Text className="text-muted">Comma-separated list of email addresses</Form.Text>
                        <Form.Control.Feedback type="invalid">{errors.additionalRecipients}</Form.Control.Feedback>
                    </Form.Group>

                    <Button type="submit" disabled={isSubmitting}>
                        Send
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
